import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, input, OnDestroy, signal, viewChild } from '@angular/core';
import {
  adaptFace, adaptHairFront, adaptHairTop, CanvasContext, computeCoverConfig, computeDetectionBounds,
  computeFaceConfig, computeFitConfig, drawPhoto,
  extractFaceLandmarks, extractHairFrontLandmarks, FaceAdapterOptions, FaceAiResponse, FaceType, FILL_ALPHA_TARGET,
  HairFrontAiResponse, HairFrontLandmarkSource, HairTopAiResponse, OUTLINED_LINE_WIDTH,
  renderFaceFull, renderHairFront, renderHairTop,
} from '@cureskin/ai-detection-renderer';
import { ApiClientConstant } from '@cureskin/api-client';
import { InstantCheckup } from '@cureskin/api-client/src/server-api';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { ReportService } from '@services/report-service/report.service';
import { UserService } from '@services/user';
import { REGIMEN_CLASS_TO_INSTANT_CHECKUP_TYPES } from '../../../app.constants';

@Component({
  selector: 'app-ai-detection-canvas',
  templateUrl: './ai-detection-canvas.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiDetectionCanvasComponent implements AfterViewInit, OnDestroy {
  readonly instantCheckup = input.required<InstantCheckup>();
  readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('c');

  readonly loading = signal(true);

  private resizeObserver: ResizeObserver | null = null;

  private readonly reportService = inject(ReportService);
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly userService = inject(UserService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly experimentService = inject(ExperimentService);

  private readonly LINE_WIDTH_REFERENCE_PX = 360;
  private readonly CANVAS_SIZE_TIMEOUT_MS = 3000;
  private readonly FACE_CROP_BOTTOM_PADDING_FACTOR = 0.1;
  private readonly FACE_TYPES: ReadonlyArray<string> = REGIMEN_CLASS_TO_INSTANT_CHECKUP_TYPES.FACE;
  private readonly HAIR_FRONT = ApiClientConstant.InstantCheckup.Type.HAIR_FRONT;
  private readonly HAIR_TOP = ApiClientConstant.InstantCheckup.Type.HAIR_TOP;

  ngAfterViewInit(): void {
    void this.render();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private waitForCanvasSize(canvas: HTMLCanvasElement): Promise<void> {
    if (canvas.clientWidth > 0 && canvas.clientHeight > 0) return Promise.resolve();
    return new Promise<void>((resolve): void => {
      let timer: ReturnType<typeof setTimeout>;
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        resolve();
      };
      this.resizeObserver = new ResizeObserver((): void => {
        if (canvas.clientWidth > 0 && canvas.clientHeight > 0) finish();
      });
      this.resizeObserver.observe(canvas);
      // Fallback: never wait (shimmer) forever — resolve after a timeout so the
      // draw runs (and degrades to a blank card via the size guard) if layout
      // never settles.
      timer = setTimeout(finish, this.CANVAS_SIZE_TIMEOUT_MS);
    });
  }

  private async render(): Promise<void> {
    try {
      await this.draw();
    } catch (error) {
      this.eventLogger.logError(error, true, 'AiDetectionCanvasComponent.render');
    } finally {
      this.loading.set(false);
    }
  }

  private async draw(): Promise<void> {
    const checkup = this.instantCheckup();
    const canvas = this.canvasRef().nativeElement;
    await this.waitForCanvasSize(canvas);
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    if (!canvas.width || !canvas.height) return;

    const aiResponse = checkup?.aiResponse as unknown as FaceAiResponse;
    const image = await this.instantCheckupService.loadImage(checkup?.imagePath);
    const dims = { height: canvas.height, width: canvas.width };

    const landmarks = extractFaceLandmarks(aiResponse);
    const faceAdaptOptions: FaceAdapterOptions | null = this.FACE_TYPES.includes(checkup?.type)
      ? {
        excludeList: this.reportService.getAiDetectionExcludeList(),
        gender: this.userService.getUserGender(),
        mainConcern: this.reportService.getConsultationSessionMainConcern(),
        type: checkup?.type as FaceType,
      }
      : null;
    // Expand the crop to contain the detections, so nothing drawn is clipped
    // (e.g. profile faces whose landmark box sits left of the cheek detections).
    const config = landmarks
      ? computeFaceConfig({
        canvas: dims,
        detectionBounds: faceAdaptOptions ? computeDetectionBounds(adaptFace(aiResponse, faceAdaptOptions)) : null,
        image,
        landmarks,
        paddingFactor: { bottom: this.FACE_CROP_BOTTOM_PADDING_FACTOR },
      })
      : computeFitConfig({ canvas: dims, image });

    const rootStyle = getComputedStyle(document.documentElement);
    const palette = {
      mint300: rootStyle.getPropertyValue('--cureskin-mint-300').trim(),
      teal350: rootStyle.getPropertyValue('--cureskin-teal-350').trim(),
    };

    const ctx = canvas.getContext('2d') as unknown as CanvasContext;
    if (faceAdaptOptions) {
      renderFaceFull(ctx, image, aiResponse, {
        ...faceAdaptOptions,
        config,
        lineDashOffset: 0,
        lineWidthScale: canvas.width / this.LINE_WIDTH_REFERENCE_PX,
        palette,
        scale: 1,
      });
    }
    if (checkup?.type === this.HAIR_FRONT) {
      // Face-crop the hair-front photo around fb_landmarks (matches checkup-view); fall back to fit.
      const hairLandmarks = extractHairFrontLandmarks(checkup?.aiResponse as unknown as HairFrontLandmarkSource);
      const hairResponse = aiResponse as unknown as HairFrontAiResponse;
      const gender = this.userService.getUserGender();
      // The AI returns ideal_hairline for all users, so the overlay is opted in
      // (package default is off) only for ideal_hairline experiment users. Also
      // passed to the bounds pass so the crop never clips the ideal line.
      const includeIdealHairline = this.experimentService.idealHairlineEnabled;
      const hairDetectionBounds = computeDetectionBounds(adaptHairFront(hairResponse, { gender, includeIdealHairline }));
      const hairConfig = hairLandmarks
        ? computeFaceConfig({
          canvas: dims,
          detectionBounds: hairDetectionBounds,
          image,
          landmarks: hairLandmarks,
          paddingFactor: { bottom: this.FACE_CROP_BOTTOM_PADDING_FACTOR },
        })
        // No fb_landmarks: cover-fill the card around the detections instead of letterboxing.
        : computeCoverConfig({ canvas: dims, detectionBounds: hairDetectionBounds, image });
      // renderHairFront is overlay-only, so draw the photo first (renderFaceFull does this internally).
      drawPhoto(ctx, image, hairConfig);
      renderHairFront(ctx, hairResponse, {
        config: hairConfig,
        gender,
        includeIdealHairline,
        lineDashOffset: 0,
        lineWidthScale: canvas.width / this.LINE_WIDTH_REFERENCE_PX,
        // Outlined mint/teal temple-baldness (+ white hairline), matching checkup-view's
        // hair-front look; without this, bare temple polygons fall to the default red.
        outlineLineWidth: OUTLINED_LINE_WIDTH,
        palette,
        scale: 1,
      });
    }
    if (checkup?.type === this.HAIR_TOP) {
      const hairTopResponse = aiResponse as unknown as HairTopAiResponse;
      // Cover-fill the card around the scalp masks (fit fallback left big side bars).
      const hairTopConfig = computeCoverConfig({
        canvas: dims,
        detectionBounds: computeDetectionBounds(adaptHairTop(hairTopResponse)),
        image,
      });
      drawPhoto(ctx, image, hairTopConfig);
      renderHairTop(ctx, hairTopResponse, {
        config: hairTopConfig,
        fillAlpha: FILL_ALPHA_TARGET,
        lineDashOffset: 0,
        palette,
        scale: 1,
      });
    }
  }
}
